import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const DRAFT_STORAGE_KEY = 'journey_drafts';
const AUTO_SAVE_DELAY = 1000;

// Default values for new journey
const defaultJourneyData = {
  // Step tracking
  currentStep: 0,
  completedSteps: [],
  
  // Template selection
  selectedTemplate: null,
  
  // Company details
  company: {
    name: '',
    registrationNumber: '',
    vatNumber: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
    contactPerson: '',
    contactNumber: '',
    emailAddress: '',
    secondaryContact: '',
    billingContactName: '',
    billingEmail: '',
    billingAddress: '',
    paymentTerms: '7 Days',
    numberOfBranches: 0,
    technicianIds: [],
  },
  
  // Branches array
  branches: [],
  
  // Onboarding configuration
  onboarding: {
    systems: [],
    companyTasks: [],
    hasServers: false,
    serverLocation: '',
    onboardingStartDate: '',
    onboardingEndDate: '',
    branchServices: {}, // { branchIndex: ['service1', 'service2'] }
  },
  
  // Metadata
  draftId: null,
  draftName: '',
  createdAt: null,
  updatedAt: null,
};

// Step definitions
export const JOURNEY_STEPS = [
  { id: 'quick-setup', title: 'Quick Setup', description: 'Choose a template or start fresh' },
  { id: 'company-details', title: 'Company Details', description: 'Basic info, contacts, and billing' },
  { id: 'branch-setup', title: 'Branch Setup', description: 'Add and configure branches' },
  { id: 'onboarding-config', title: 'Onboarding', description: 'Systems, tasks, and services' },
  { id: 'review', title: 'Review & Launch', description: 'Confirm and create' },
];

// Internal systems options
export const INTERNAL_SYSTEMS = ['PSA', 'AEM', 'Fincon', 'IT Glue'];

// Company-wide tasks options
export const COMPANY_TASKS = [
  'Email Migration',
  'Domain DNS',
  'Website Migration',
  'Include Backups',
  'Acronis Advanced Email Security',
];

// Branch services options
export const BRANCH_SERVICES = [
  'Install MiBOT Agent',
  'Install Managed AV',
  'Setup Office 365 / Mail',
  'Setup OneDrive',
  'Install CyberFox',
  'Install Acronis Backup Agent',
];

// Server location options
export const SERVER_LOCATIONS = ['Head Office', 'Cloud Based', 'Per Branch'];

// Payment terms options
export const PAYMENT_TERMS = ['7 Days', '14 Days', '30 Days', 'Month End'];

const JourneyContext = createContext(null);

export function JourneyProvider({ children }) {
  const [journeyData, setJourneyData] = useState({ ...defaultJourneyData });
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const autoSaveTimerRef = useRef(null);

  // Load all drafts from localStorage
  const loadDrafts = useCallback(() => {
    try {
      const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setDrafts(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error('Error loading drafts:', error);
      setDrafts([]);
    }
  }, []);

  // Save current draft to localStorage
  const saveDraft = useCallback((name = null) => {
    const draftId = journeyData.draftId || `draft_${Date.now()}`;
    const draftName = name || journeyData.draftName || journeyData.company.name || 'Untitled Draft';
    const now = new Date().toISOString();

    const draftData = {
      ...journeyData,
      draftId,
      draftName,
      createdAt: journeyData.createdAt || now,
      updatedAt: now,
    };

    try {
      const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
      let allDrafts = stored ? JSON.parse(stored) : [];
      
      const existingIndex = allDrafts.findIndex(d => d.draftId === draftId);
      if (existingIndex >= 0) {
        allDrafts[existingIndex] = draftData;
      } else {
        allDrafts.unshift(draftData);
      }

      // Keep only last 10 drafts
      allDrafts = allDrafts.slice(0, 10);
      
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(allDrafts));
      setDrafts(allDrafts);
      setJourneyData(prev => ({ ...prev, draftId, draftName }));
      setIsDirty(false);
      
      return draftId;
    } catch (error) {
      console.error('Error saving draft:', error);
      return null;
    }
  }, [journeyData]);

  // Load a specific draft
  const loadDraft = useCallback((draftId) => {
    const draft = drafts.find(d => d.draftId === draftId);
    if (draft) {
      setJourneyData(draft);
      setIsDirty(false);
      return true;
    }
    return false;
  }, [drafts]);

  // Reset to fresh state
  const resetJourney = useCallback(() => {
    setJourneyData({ ...defaultJourneyData });
    setIsDirty(false);
  }, []);

  // Delete a draft
  const deleteDraft = useCallback((draftId) => {
    try {
      const stored = localStorage.getItem(DRAFT_STORAGE_KEY);
      let allDrafts = stored ? JSON.parse(stored) : [];
      allDrafts = allDrafts.filter(d => d.draftId !== draftId);
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(allDrafts));
      setDrafts(allDrafts);
      
      if (journeyData.draftId === draftId) {
        resetJourney();
      }
      return true;
    } catch (error) {
      console.error('Error deleting draft:', error);
      return false;
    }
  }, [journeyData.draftId, resetJourney]);

  // Load drafts from localStorage on mount
  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  // Auto-save effect
  useEffect(() => {
    if (isDirty && journeyData.draftId) {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      autoSaveTimerRef.current = setTimeout(() => {
        saveDraft();
      }, AUTO_SAVE_DELAY);
    }
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [isDirty, journeyData, saveDraft]);

  // Start a new draft
  const startNewDraft = useCallback((templateData = null) => {
    const newData = {
      ...defaultJourneyData,
      draftId: `draft_${Date.now()}`,
      createdAt: new Date().toISOString(),
      ...(templateData || {}),
    };
    setJourneyData(newData);
    setIsDirty(true);
    return newData.draftId;
  }, []);

  // Update company data
  const updateCompany = useCallback((updates) => {
    setJourneyData(prev => ({
      ...prev,
      company: { ...prev.company, ...updates },
    }));
    setIsDirty(true);
  }, []);

  // Update branches
  const updateBranches = useCallback((branches) => {
    setJourneyData(prev => ({
      ...prev,
      branches: Array.isArray(branches) ? branches : prev.branches,
    }));
    setIsDirty(true);
  }, []);

  // Add a branch
  const addBranch = useCallback((branch) => {
    setJourneyData(prev => ({
      ...prev,
      branches: [...prev.branches, { ...branch, id: `branch_${Date.now()}` }],
    }));
    setIsDirty(true);
  }, []);

  // Update a specific branch
  const updateBranch = useCallback((index, updates) => {
    setJourneyData(prev => ({
      ...prev,
      branches: prev.branches.map((b, i) => i === index ? { ...b, ...updates } : b),
    }));
    setIsDirty(true);
  }, []);

  // Remove a branch
  const removeBranch = useCallback((index) => {
    setJourneyData(prev => ({
      ...prev,
      branches: prev.branches.filter((_, i) => i !== index),
    }));
    setIsDirty(true);
  }, []);

  // Update onboarding config
  const updateOnboarding = useCallback((updates) => {
    setJourneyData(prev => ({
      ...prev,
      onboarding: { ...prev.onboarding, ...updates },
    }));
    setIsDirty(true);
  }, []);

  // Navigation
  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < JOURNEY_STEPS.length) {
      setJourneyData(prev => ({
        ...prev,
        currentStep: stepIndex,
      }));
    }
  }, []);

  const nextStep = useCallback(() => {
    setJourneyData(prev => {
      const next = Math.min(prev.currentStep + 1, JOURNEY_STEPS.length - 1);
      const completed = prev.completedSteps.includes(prev.currentStep)
        ? prev.completedSteps
        : [...prev.completedSteps, prev.currentStep];
      return {
        ...prev,
        currentStep: next,
        completedSteps: completed,
      };
    });
  }, []);

  const prevStep = useCallback(() => {
    setJourneyData(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 0),
    }));
  }, []);

  // Apply template
  const applyTemplate = useCallback((template) => {
    if (!template) return;
    
    setJourneyData(prev => ({
      ...prev,
      selectedTemplate: template.id,
      onboarding: {
        ...prev.onboarding,
        systems: template.defaults?.systems || [],
        companyTasks: template.defaults?.tasks || [],
        hasServers: template.defaults?.hasServers ?? false,
      },
      company: {
        ...prev.company,
        ...(template.defaults?.company || {}),
      },
    }));
    setIsDirty(true);
  }, []);

  // Calculate totals for preview
  const getTotals = useCallback(() => {
    const branches = journeyData.branches;
    return {
      branchCount: branches.length,
      totalUsers: branches.reduce((sum, b) => sum + (parseInt(b.numberOfUsers) || 0), 0),
      totalDevices: branches.reduce((sum, b) => sum + (parseInt(b.numberOfDevices) || 0), 0),
      totalNetworkDevices: branches.reduce((sum, b) => sum + (parseInt(b.numberOfNetworkDevices) || 0), 0),
      totalServers: branches.reduce((sum, b) => sum + (parseInt(b.numberOfServers) || 0), 0),
      hasFirewall: branches.some(b => b.hasFirewall),
      selectedSystems: journeyData.onboarding.systems.length,
      selectedTasks: journeyData.onboarding.companyTasks.length,
    };
  }, [journeyData.branches, journeyData.onboarding]);

  const value = {
    // State
    journeyData,
    isDirty,
    isLoading,
    drafts,
    currentStep: journeyData.currentStep,
    completedSteps: journeyData.completedSteps,
    
    // Company
    company: journeyData.company,
    updateCompany,
    
    // Branches
    branches: journeyData.branches,
    updateBranches,
    addBranch,
    updateBranch,
    removeBranch,
    
    // Onboarding
    onboarding: journeyData.onboarding,
    updateOnboarding,
    
    // Navigation
    goToStep,
    nextStep,
    prevStep,
    
    // Templates
    selectedTemplate: journeyData.selectedTemplate,
    applyTemplate,
    
    // Drafts
    saveDraft,
    loadDraft,
    deleteDraft,
    loadDrafts,
    startNewDraft,
    resetJourney,
    
    // Utilities
    getTotals,
    setIsLoading,
  };

  return (
    <JourneyContext.Provider value={value}>
      {children}
    </JourneyContext.Provider>
  );
}

export function useJourney() {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error('useJourney must be used within a JourneyProvider');
  }
  return context;
}

export default JourneyContext;

